import { useEffect, useState } from 'preact/hooks';
import { fetchWebzineIssues } from '@/lib/webzine.api.ts';
import { MSG } from '@/lib/constants.ts';
import type { WebzineIssue } from '@/types/webzine.ts';

/**
 * 웹진 목록.
 *
 * 홈 · 목록 · 지난 호가 모두 같은 목록을 쓴다. 화면을 옮길 때마다 다시 부르면
 * 같은 데이터를 계속 내려받게 되므로 **요청 자체를 모듈에 담아 둔다.**
 * (Promise 를 캐시하면 동시에 여러 화면이 떠도 요청은 한 번만 나간다.)
 */
let cache: Promise<WebzineIssue[]> | null = null;

async function load(): Promise<WebzineIssue[]> {
  const response = await fetchWebzineIssues(1);
  if (!response.result) throw new Error(response.message ?? MSG.apiFail);
  return response.data?.items ?? [];
}

/** 캐시된 목록을 준다. 실패하면 다음 호출에서 다시 시도할 수 있게 캐시를 비운다. */
export function loadWebzineIssues(): Promise<WebzineIssue[]> {
  if (!cache) {
    cache = load().catch((error: unknown) => {
      cache = null;
      throw error;
    });
  }
  return cache;
}

/** 관리 화면에서 내용을 고친 뒤 새로 받아야 할 때 */
export function clearWebzineCache(): void {
  cache = null;
}

export interface WebzineState {
  issues: WebzineIssue[];
  /** 최신 호. 홈이 보여주는 호다. */
  latest: WebzineIssue | null;
  loading: boolean;
  error: string | null;
}

export function useWebzineIssues(): WebzineState {
  const [issues, setIssues] = useState<WebzineIssue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    loadWebzineIssues()
      .then((items) => {
        if (cancelled) return;
        setIssues(items);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : MSG.apiFail);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { issues, latest: issues[0] ?? null, loading, error };
}
